/**
 * Гео-математика. Формулы haversine/bearing — эталон из TecTactic tactical_geo.dart
 * (R=6371000, asin с clamp) — согласованы с JS-стороной донора бит-в-бит.
 * Координаты — GeoJSON-порядок [lon, lat] (как в RVF).
 */

export type LonLat = readonly [number, number]

const R = 6371000
const toRad = (deg: number): number => (deg * Math.PI) / 180
const toDeg = (rad: number): number => (rad * 180) / Math.PI

/** Дистанция в метрах (haversine). */
export function haversineM(a: LonLat, b: LonLat): number {
  const dLat = toRad(b[1] - a[1])
  const dLon = toRad(b[0] - a[0])
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

/** Азимут в градусах 0..360 (0 = север). */
export function bearingDeg(a: LonLat, b: LonLat): number {
  const φ1 = toRad(a[1])
  const φ2 = toRad(b[1])
  const dLon = toRad(b[0] - a[0])
  const y = Math.sin(dLon) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dLon)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

/**
 * Точка в `distanceM` метрах от `from` по азимуту `bearing` (0 = север) —
 * прямая геодезическая задача на сфере, обратная к haversineM/bearingDeg.
 * Нужна аварийному режиму: «отойдите на N м вот сюда» из зоны опасности.
 */
export function destinationPoint(from: LonLat, bearing: number, distanceM: number): LonLat {
  const δ = distanceM / R
  const θ = toRad(bearing)
  const φ1 = toRad(from[1])
  const λ1 = toRad(from[0])
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ))
  const λ2 =
    λ1 +
    Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2))
  return [((toDeg(λ2) + 540) % 360) - 180, toDeg(φ2)]
}

/** Разница азимутов в диапазоне -180..180 (знак — направление поворота). */
export function bearingDiffDeg(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180
}

/** Длина полилинии в метрах. */
export function polylineLengthM(points: readonly LonLat[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const cur = points[i]
    if (prev && cur) total += haversineM(prev, cur)
  }
  return total
}

/**
 * Площадь кольца в м² (сферическая формула, знак отброшен).
 *
 * Нужна редактору: обвёл помещение — площадь посчиталась сама, вручную её
 * никто не меряет. На масштабе площадки (сотни метров) сферическая формула
 * практически совпадает с плоской, но не врёт на больших территориях.
 */
export function ringAreaM2(ring: readonly LonLat[]): number {
  if (ring.length < 3) return 0
  let sum = 0
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]
    const b = ring[(i + 1) % ring.length]
    if (!a || !b) continue
    sum += toRad(b[0] - a[0]) * (2 + Math.sin(toRad(a[1])) + Math.sin(toRad(b[1])))
  }
  return Math.abs((sum * R * R) / 2)
}

/**
 * Денсификация полилинии до шага ≤ stepM.
 * Требование MapMatcher (матчит к вершинам): шаг 5–10 м на масштабе площадки.
 */
export function densifyPolyline(points: readonly LonLat[], stepM: number): LonLat[] {
  if (points.length < 2) return [...points]
  const out: LonLat[] = []
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]
    const b = points[i]
    if (!a || !b) continue
    out.push(a)
    const dist = haversineM(a, b)
    const n = Math.floor(dist / stepM)
    for (let k = 1; k <= n; k++) {
      const t = k / (n + 1)
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
  }
  const last = points[points.length - 1]
  if (last) out.push(last)
  return out
}

/**
 * Проекция точки на сегмент (равнопромежуточная аппроксимация — корректна на
 * масштабе площадки, сотни метров). Возвращает точку и дистанцию до неё в метрах.
 */
export function projectOnSegment(
  p: LonLat,
  a: LonLat,
  b: LonLat,
): { point: LonLat; distanceM: number; t: number } {
  const cosLat = Math.cos(toRad(a[1]))
  const ax = a[0] * cosLat
  const bx = b[0] * cosLat
  const px = p[0] * cosLat
  const dx = bx - ax
  const dy = b[1] - a[1]
  const lenSq = dx * dx + dy * dy
  const t =
    lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (p[1] - a[1]) * dy) / lenSq))
  const point: LonLat = [a[0] + (b[0] - a[0]) * t, a[1] + dy * t]
  return { point, distanceM: haversineM(p, point), t }
}

export { lestToWgs84 } from './lest97'
export { decodePolyline, encodePolyline, simplifyPolyline } from './polyline'
